import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  NoPreloading,
  provideRouter,
  Router,
  withHashLocation,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideMomentDatetimeAdapter } from '@ng-matero/extensions-moment-adapter';
import { MTX_DATETIME_FORMATS } from '@ng-matero/extensions/core';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, RouterState } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import player from 'lottie-web';
import { provideLottieOptions } from 'ngx-lottie';
import { provideToastr } from 'ngx-toastr';

import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { setHeadersInterceptor } from './core/interceptors/set-headers.interceptor';
import { StartupService } from './core/services/startup.service';
import { AuthEffects } from './core/state/auth-state/auth.effects';
import { metaReducers, reducers } from './core/state/core.reducers';
import { GlobalEntitiesEffects } from './core/state/global-entities-state/global-entities.effects';

function translateHttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),

    provideRouter(
      appRoutes,
      withPreloading(NoPreloading),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      ...(environment.use_hash_for_routing ? [withHashLocation()] : [])
    ),

    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.env === 'prod',
      registrationStrategy: 'registerWhenStable:10000',
    }),

    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),

    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictActionSerializability: true,
        strictStateSerializability: true,
      },
    }),
    provideEffects([AuthEffects, GlobalEntitiesEffects]),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
    provideRouterStore({ stateKey: 'router', routerState: RouterState.Minimal }),

    provideToastr({ positionClass: 'toast-bottom-right' }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: translateHttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    provideAngularSvgIcon(),

    provideAppInitializer(() => {
      const startupService = inject(StartupService);
      return startupService.load();
    }),
    { provide: LOCALE_ID, useValue: environment.locale },

    provideHttpClient(withInterceptors([setHeadersInterceptor, authInterceptor])),

    provideLottieOptions({ player: () => player }),
    provideMomentDatetimeAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MTX_DATETIME_FORMATS },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
  ],
};
